import { useEffect, useRef, useState } from "react";
import type { AdminEmailMessageResponse } from "../models/admin-email.models";
import { getAdminEmailErrorMessage, getAdminEmailMessage } from "../services/admin-email.service";

// Read a selected message without changing its mailbox read flag.
// The body is fetched separately from list previews and is released when this reader unmounts.
export const useAdminEmailMessage = (mailboxId: string, messageId: string) => {
  const [response, setResponse] = useState<AdminEmailMessageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [continuationError, setContinuationError] = useState<string | null>(null);
  const requestController = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    requestController.current = controller;
    setResponse(null);
    setLoading(true);
    setError(null);
    setLoadingMore(false);
    setContinuationError(null);

    getAdminEmailMessage(mailboxId, messageId, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          setResponse(result);
        }
      })
      .catch((requestError) => {
        if (!controller.signal.aborted) {
          setError(getAdminEmailErrorMessage(requestError));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => {
      controller.abort();
      requestController.current = null;
    };
  }, [mailboxId, messageId]);

  // === EMAIL ===

  const loadMore = async () => {
    const offset = response?.message.nextBodyOffset;
    const controller = requestController.current;

    if (loading || loadingMore || offset === null || offset === undefined || !controller || controller.signal.aborted) {
      return;
    }

    setLoadingMore(true);
    setContinuationError(null);

    try {
      const result = await getAdminEmailMessage(mailboxId, messageId, controller.signal, offset);

      if (!controller.signal.aborted) {
        if (result.message.bodyOffset !== offset || result.message.id !== messageId) {
          throw new Error("The message content could not be continued. Refresh the inbox and try again.");
        }

        setResponse((current) => {
          if (!current || current.message.nextBodyOffset !== offset || result.message.id !== current.message.id) {
            return current;
          }

          return {
            ...result,
            message: {
              ...result.message,
              body: current.message.body + result.message.body,
              bodyOffset: 0,
              bodyTruncated: result.message.nextBodyOffset !== undefined && result.message.nextBodyOffset !== null,
            },
          };
        });
      }
    } catch (requestError) {
      if (!controller.signal.aborted) {
        setContinuationError(getAdminEmailErrorMessage(requestError));
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoadingMore(false);
      }
    }
  };

  return { response, loading, error, loadingMore, continuationError, loadMore };
};
